import { Download, ExternalLink, ImagePlus } from "lucide-react";
import { Reveal, Label } from "../components/site/Reveal";
import { AppStoreBadge } from "../components/site/AppStoreBadge";
import { SITE } from "../config/site";

const boilerplateShort =
  "NXTPlate is an AI-powered nutrition app for iPhone that tracks what you eat, shows what you still need, and helps guide your next meal.";

const boilerplateLong =
  "NXTPlate is a nutrition and wellness app for iOS built around a simple idea: your next plate, already planned. It combines photo-based food logging, macro and calorie tracking, and a live view of your remaining nutrition for the day with personalized next-meal guidance. The NXTPlate AI Coach adapts recommendations to each user's targets, trends, and preferences — turning nutrition data into a practical next step instead of another spreadsheet. NXTPlate is available on the App Store with free access, NXTPlate Pro ($14.99/month or $143.99/year), and NXTPlate AI Coach ($24.99/month or $239.99/year). NXTPlate is a wellness app and does not provide medical advice, diagnosis, or treatment.";

const facts = [
  ["Platform", "iOS (iPhone)"],
  ["Category", "Nutrition & wellness"],
  ["Price", "Free · Pro $14.99/mo · AI Coach $24.99/mo"],
  ["Website", SITE.domain],
  ["Contact", SITE.supportEmail],
];

const assets = [
  { name: "App icon (512×512 PNG)", file: "/press/nxtplate-icon-512.png", preview: "/press/nxtplate-icon-512.png", imgClass: "h-16 w-16 rounded-2xl" },
  { name: "Wordmark (transparent PNG)", file: "/press/nxtplate-wordmark.png", preview: "/press/nxtplate-wordmark-cream.png", imgClass: "h-10 w-auto" },
  { name: "Wordmark on cream (PNG)", file: "/press/nxtplate-wordmark-cream.png", preview: "/press/nxtplate-wordmark-cream.png", imgClass: "h-10 w-auto" },
  { name: "Share card (1200×630 PNG)", file: "/og-image.png", preview: "/og-image.png", imgClass: "h-24 w-auto rounded-lg" },
  { name: "Icon (SVG)", file: "/favicon.svg", preview: "/favicon.svg", imgClass: "h-16 w-16" },
];

const colors = [
  { name: "Coral", hex: "#FF6B57", text: "text-white" },
  { name: "Teal", hex: "#2A9D8F", text: "text-white" },
  { name: "Cream", hex: "#FDFCF8", text: "text-neutral-900" },
  { name: "Ink", hex: "#171717", text: "text-white" },
];

const Section = ({ label, title, children, testId }) => (
  <Reveal className="border-t border-neutral-200 py-14 first:border-t-0 first:pt-0">
    <section data-testid={testId}>
      <Label>{label}</Label>
      <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-neutral-900 md:text-4xl">{title}</h2>
      <div className="mt-8">{children}</div>
    </section>
  </Reveal>
);

export default function Press() {
  return (
    <main className="bg-cream">
      <section className="noise relative overflow-hidden pt-32 md:pt-44 pb-12">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Reveal><Label>Press</Label></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl font-medium tracking-tight text-neutral-900 md:text-6xl">
              NXTPlate <span className="italic text-coral">Press Kit</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-600 md:text-lg">
              Everything you need to write about NXTPlate — the story, the facts, brand assets, and
              where to get the app.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <AppStoreBadge testId="press-app-store-badge" />
              <a
                href={SITE.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="press-app-store-link"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors duration-200 hover:text-coral"
              >
                {SITE.appStoreUrl.replace("https://", "")}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Section label="Boilerplate" title="About NXTPlate" testId="press-boilerplate">
            <div className="space-y-6">
              <p className="max-w-3xl font-display text-xl italic leading-relaxed text-neutral-800 md:text-2xl">
                "{boilerplateShort}"
              </p>
              <p className="max-w-3xl text-base leading-relaxed text-neutral-600">{boilerplateLong}</p>
            </div>
          </Section>

          <Section label="Fast facts" title="The essentials" testId="press-facts">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {facts.map(([k, v]) => (
                <div key={k} className="rounded-2xl bg-bone px-6 py-5">
                  <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400">{k}</dt>
                  <dd className="mt-1.5 text-sm font-medium text-neutral-900">{v}</dd>
                </div>
              ))}
            </dl>
          </Section>

          <Section label="Brand assets" title="Logos & imagery" testId="press-assets">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {assets.map((a) => (
                <div
                  key={a.name}
                  data-testid={`press-asset-${a.file.split("/").pop().replace(/\W+/g, "-")}`}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-neutral-200/70 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-bone">
                      <img src={a.preview} alt={a.name} className={a.imgClass} loading="lazy" />
                    </div>
                    <p className="text-sm font-medium text-neutral-900">{a.name}</p>
                  </div>
                  <a
                    href={a.file}
                    download
                    data-testid={`press-download-${a.file.split("/").pop().replace(/\W+/g, "-")}`}
                    aria-label={`Download ${a.name}`}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition-all duration-200 hover:scale-105 hover:bg-coral"
                  >
                    <Download className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Brand colors</p>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {colors.map((c) => (
                  <div
                    key={c.name}
                    data-testid={`press-color-${c.name.toLowerCase()}`}
                    className="overflow-hidden rounded-2xl border border-neutral-200/70"
                  >
                    <div className="h-16" style={{ backgroundColor: c.hex }} />
                    <div className="bg-white px-4 py-3">
                      <p className="text-sm font-semibold text-neutral-900">{c.name}</p>
                      <p className="text-xs text-neutral-500">{c.hex}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          <Section label="Product shots" title="App screenshots" testId="press-screenshots">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {["shot-1.png", "shot-2.png", "shot-3.png"].map((f, i) => (
                <div
                  key={f}
                  data-testid={`press-screenshot-slot-${i + 1}`}
                  className="flex aspect-[9/19] flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-neutral-200 bg-cream p-6 text-center"
                >
                  <ImagePlus className="h-6 w-6 text-neutral-300" aria-hidden="true" />
                  <p className="text-xs leading-relaxed text-neutral-400">
                    Slot {i + 1} — reserved for an iPhone screenshot. Share your App Store
                    screenshots and I'll wire them in here.
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-neutral-500">
              Tip: the cleanest screenshots come straight from App Store Connect or your App Store
              listing. Media enquiries:{" "}
              <a
                href={`mailto:${SITE.supportEmail}`}
                data-testid="press-email-link"
                className="font-semibold text-neutral-900 underline decoration-coral decoration-2 underline-offset-4 transition-colors duration-200 hover:text-coral"
              >
                {SITE.supportEmail}
              </a>
              .
            </p>
          </Section>
        </div>
      </section>
    </main>
  );
}
